import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { colors, viz } from "../../styles";
import { formatCompactNumber } from "../../format";
import { SeriesDef } from "../ChartCard";
import ChartTooltip from "../ChartTooltip";

/** Single- or multi-series trend line: 2px stroke, ≥8px hover markers. */
const TrendLine = <T extends Record<string, any>>({
  data,
  series,
  categoryKey,
  height = 220,
  tickFormatter,
  labelFormatter,
  valueFormatter,
  valueTickFormatter = formatCompactNumber,
}: {
  data: T[];
  series: SeriesDef[];
  categoryKey: string;
  height?: number;
  tickFormatter?: (value: string) => string;
  labelFormatter?: (value: string) => string;
  valueFormatter?: (value: number) => string;
  /**
   * Formats the VALUE (Y) axis. Defaults to compact numbers.
   *
   * A line chart plotting MONEY must pass `formatCentsCompact`, exactly as
   * `StackedColumns` already documents: every money figure this dashboard stores
   * is in cents, so a raw axis reads a hundred times too large. This prop was
   * added when the recurring-revenue chart became the first line chart here to
   * plot cents — until then every TrendLine drew counts, so the hardcoded
   * default was correct by accident rather than by design.
   */
  valueTickFormatter?: (value: number) => string;
}) => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
      <CartesianGrid stroke={viz.gridline} vertical={false} />
      <XAxis
        dataKey={categoryKey}
        tickFormatter={tickFormatter}
        tick={{ fill: viz.axisInk, fontSize: 11 }}
        tickLine={false}
        axisLine={{ stroke: viz.axis }}
        interval="preserveStartEnd"
        minTickGap={16}
      />
      <YAxis
        tick={{ fill: viz.axisInk, fontSize: 11 }}
        tickLine={false}
        axisLine={false}
        width={44}
        // Same 44px gutter, same clipping risk as StackedColumns — abbreviated
        // for the same reason, so the two chart types cannot disagree about
        // what a Y tick looks like.
        tickFormatter={valueTickFormatter}
      />
      <Tooltip
        cursor={{ stroke: viz.axis, strokeWidth: 1 }}
        content={
          <ChartTooltip labelFormatter={labelFormatter} valueFormatter={valueFormatter} />
        }
      />
      {series.map(item => (
        <Line
          key={item.key}
          type="monotone"
          dataKey={item.key}
          name={item.label}
          stroke={item.color}
          strokeWidth={2}
          dot={false}
          // A 2px surface ring keeps the marker readable where it overlaps
          // gridlines or another series.
          activeDot={{ r: 5, strokeWidth: 2, stroke: colors.surface }}
        />
      ))}
    </LineChart>
  </ResponsiveContainer>
);

export default TrendLine;
